<div class="shift-dialog">
    <div class="dialog-header">
        <h2 mat-dialog-title>{{ data.shift?.id ? 'Edit Shift' : 'Add Shift' }} - {{ employeeDisplayName }}</h2>
        <button mat-icon-button (click)="onCancel()" class="close-btn">
            <mat-icon>close</mat-icon>
        </button>
    </div>

    <form [formGroup]="form" mat-dialog-content>
        <!-- Employee & Date Info -->
        <div class="info-row">
            <span class="info-item"><strong>Date:</strong> {{ formattedDate }}</span>
            <span class="info-item"><strong>Job Title:</strong> {{ data.employee.title_name }}</span>

            <!-- Shop Display -->
            <mat-form-field appearance="outline" class="shop-field">
                <mat-label>Shop</mat-label>
                <input matInput [value]="getSelectedShopDisplayValue()" readonly>
            </mat-form-field>
        </div>

        <!-- Warning Message Banner -->
        <div class="warning-banner" *ngIf="warningMessage" [ngClass]="warningMessage.color"
            style="display: flex; flex-direction: column; align-items: flex-start; gap: 4px;">
            <div *ngFor="let msg of warningMessage.texts">{{ msg }}</div>
        </div>

        <ng-container *ngIf="data.isPastOrCurrentWeek; else rosterOnly">
            <mat-tab-group class="shift-dialog-tabs" [selectedIndex]="activeTabIndex" (selectedTabChange)="activeTabIndex = $event.index">
                <mat-tab label="Roster Shift" *ngIf="showRosterTab">
                    <div class="tab-content roster-tab-content">
                        <div class="row">
                            <!-- Shift Template, From and To in a single line -->
                            <mat-form-field appearance="outline" class="template-field">
                                <mat-label>Shift Template</mat-label>
                                <mat-select formControlName="shift_template_id">
                                    <mat-option value="" *ngIf="isPastOrCurrentDate()">None</mat-option>
                                    <mat-option value="custom">Custom Shift</mat-option>
                                    <mat-option *ngFor="let s of data.masterShifts" [value]="s.id">
                                        {{ s.name }} ({{ s.start_time.slice(0, 5) }} - {{ s.end_time.slice(0, 5) }})
                                    </mat-option>
                                </mat-select>
                            </mat-form-field>

                            <div class="time-field-group">
                                <mat-form-field appearance="outline">
                                    <mat-label>HH</mat-label>
                                    <mat-select [value]="start_hour" panelClass="time-select-panel" (selectionChange)="start_hour = $event.value; updateStartTime()" [disabled]="!!form.get('start_time')?.disabled">
                                        <mat-option *ngFor="let h of hours" [value]="h">{{ h }}</mat-option>
                                    </mat-select>
                                </mat-form-field>
                                <span class="time-separator">:</span>
                                <mat-form-field appearance="outline">
                                    <mat-label>MM</mat-label>
                                    <mat-select [value]="start_min" panelClass="time-select-panel" (selectionChange)="start_min = $event.value; updateStartTime()" [disabled]="!!form.get('start_time')?.disabled">
                                        <mat-option *ngFor="let m of minutes" [value]="m">{{ m }}</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>

                            <div class="time-field-group">
                                <mat-form-field appearance="outline">
                                    <mat-label>HH</mat-label>
                                    <mat-select [value]="end_hour" panelClass="time-select-panel" (selectionChange)="end_hour = $event.value; updateEndTime()" [disabled]="!!form.get('end_time')?.disabled">
                                        <mat-option *ngFor="let h of hours" [value]="h">{{ h }}</mat-option>
                                    </mat-select>
                                </mat-form-field>
                                <span class="time-separator">:</span>
                                <mat-form-field appearance="outline">
                                    <mat-label>MM</mat-label>
                                    <mat-select [value]="end_min" panelClass="time-select-panel" (selectionChange)="end_min = $event.value; updateEndTime()" [disabled]="!!form.get('end_time')?.disabled">
                                        <mat-option *ngFor="let m of minutes" [value]="m">{{ m }}</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                        </div>

                        <!-- Modern Task Selection (Chip-based) -->
                        <div class="task-selection-container">
                            <div class="label-row">
                                <label class="section-label">Select Tasks {{ form.get('tasks')?.value?.length || 0
                                    }}/{{ getTaskLimit() }}</label>
                            </div>

                            <!-- Glowing Toaster Notification -->
                            <div class="limit-toast" [class.show]="showLimitError" [style.top.px]="toastY">
                                <mat-icon>warning</mat-icon>
                                <span>Cant add more tasks. Remove one</span>
                            </div>

                            <mat-selection-list formControlName="tasks"
                                (selectionChange)="onTaskSelectionChange($event)" class="task-chips-container"
                                [multiple]="true" [disabled]="isShiftReadOnly || !!form.get('tasks')?.disabled">
                                <mat-list-option *ngFor="let task of data.tasks" [value]="task.id"
                                    [class.selected]="form.get('tasks')?.value?.includes(task.id)"
                                    [style.--task-bg]="task.bg_color" [style.--task-fg]="task.fg_color">
                                    <div class="modern-chip">
                                        <span class="task-name">{{ task.name }}</span>
                                        <mat-icon *ngIf="form.get('tasks')?.value?.includes(task.id)"
                                            class="check-icon">close</mat-icon>
                                    </div>
                                </mat-list-option>
                            </mat-selection-list>
                        </div>

                        <div class="checkbox-row">
                            <mat-checkbox formControlName="is_in_charge">In Charge (INC)</mat-checkbox>
                        </div>

                        <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Comments</mat-label>
                            <textarea matInput formControlName="notes" rows="3"
                                placeholder="Add shift notes..."></textarea>
                        </mat-form-field>
                    </div>
                </mat-tab>
                <mat-tab label="Actual Attendance" *ngIf="isPastOrCurrentDate() && canViewAttendance">
                    <div class="tab-content attendance-tab-content">
                        <div class="row">
                            <div class="time-field-group">
                                <mat-form-field appearance="outline">
                                    <mat-label>HH</mat-label>
                                    <input matInput type="number" min="0" max="23" [value]="act_start_hour" (input)="act_start_hour = $any($event.target).value; updateActStartTime()" [disabled]="isAttendanceReadOnly || !!form.get('act_shift_start_time')?.disabled" placeholder="HH">
                                </mat-form-field>
                                <span class="time-separator">:</span>
                                <mat-form-field appearance="outline">
                                    <mat-label>MM</mat-label>
                                    <input matInput type="number" min="0" max="59" [value]="act_start_min" (input)="act_start_min = $any($event.target).value; updateActStartTime()" [disabled]="isAttendanceReadOnly || !!form.get('act_shift_start_time')?.disabled" placeholder="MM">
                                </mat-form-field>
                            </div>

                            <div class="time-field-group">
                                <mat-form-field appearance="outline">
                                    <mat-label>HH</mat-label>
                                    <input matInput type="number" min="0" max="23" [value]="act_end_hour" (input)="act_end_hour = $any($event.target).value; updateActEndTime()" [disabled]="isAttendanceReadOnly || !!form.get('act_shift_end_time')?.disabled" placeholder="HH">
                                </mat-form-field>
                                <span class="time-separator">:</span>
                                <mat-form-field appearance="outline">
                                    <mat-label>MM</mat-label>
                                    <input matInput type="number" min="0" max="59" [value]="act_end_min" (input)="act_end_min = $any($event.target).value; updateActEndTime()" [disabled]="isAttendanceReadOnly || !!form.get('act_shift_end_time')?.disabled" placeholder="MM">
                                </mat-form-field>
                            </div>

                            <mat-form-field appearance="outline" class="time-field">
                                <mat-label>Unpaid Break (HH:MM)</mat-label>
                                <input matInput formControlName="upb" [readonly]="isAttendanceReadOnly">
                            </mat-form-field>
                        </div>

                        <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Adjustment Reason</mat-label>
                            <mat-select formControlName="entry_reason" [disabled]="isAttendanceReadOnly || !!form.get('entry_reason')?.disabled">
                                <mat-option [value]="null">Select Reason</mat-option>
                                <mat-option *ngFor="let r of (data.reasons || [])" [value]="r.id">{{ r.description }}</mat-option>
                                <mat-option *ngIf="!hasOtherInReasons" [value]="'other'">Other</mat-option>
                            </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="full-width"
                            *ngIf="isOtherReasonSelected()">
                            <mat-label>Specify Other Reason</mat-label>
                            <textarea matInput rows="3" formControlName="entry_reason_others"
                                [readonly]="isAttendanceReadOnly"
                                placeholder="Reason details..."></textarea>
                        </mat-form-field>

                        <div class="checkbox-row" style="margin-top: 4px;" *ngIf="showAbsentCheckbox">
                            <mat-checkbox formControlName="is_absent" color="warn" [disabled]="isAttendanceReadOnly || !!form.get('is_absent')?.disabled" style="font-weight: bold; color: #dc2626; font-size: 18px;">Absent</mat-checkbox>
                        </div>
                    </div>
                </mat-tab>
            </mat-tab-group>
        </ng-container>

        <ng-template #rosterOnly>
            <div class="row">
                <!-- Shift Template, From and To in a single line -->
                <mat-form-field appearance="outline" class="template-field">
                    <mat-label>Shift Template</mat-label>
                    <mat-select formControlName="shift_template_id">
                        <mat-option value="" *ngIf="isPastOrCurrentDate()">None</mat-option>
                        <mat-option value="custom">Custom Shift</mat-option>
                        <mat-option *ngFor="let s of data.masterShifts" [value]="s.id">
                            {{ s.name }} ({{ s.start_time.slice(0, 5) }} - {{ s.end_time.slice(0, 5) }})
                        </mat-option>
                    </mat-select>
                </mat-form-field>

                <div class="time-field-group">
                    <mat-form-field appearance="outline">
                        <mat-label>HH</mat-label>
                        <mat-select [value]="start_hour" panelClass="time-select-panel" (selectionChange)="start_hour = $event.value; updateStartTime()" [disabled]="!!form.get('start_time')?.disabled">
                            <mat-option *ngFor="let h of hours" [value]="h">{{ h }}</mat-option>
                        </mat-select>
                    </mat-form-field>
                    <span class="time-separator">:</span>
                    <mat-form-field appearance="outline">
                        <mat-label>MM</mat-label>
                        <mat-select [value]="start_min" panelClass="time-select-panel" (selectionChange)="start_min = $event.value; updateStartTime()" [disabled]="!!form.get('start_time')?.disabled">
                            <mat-option *ngFor="let m of minutes" [value]="m">{{ m }}</mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>

                <div class="time-field-group">
                    <mat-form-field appearance="outline">
                        <mat-label>HH</mat-label>
                        <mat-select [value]="end_hour" panelClass="time-select-panel" (selectionChange)="end_hour = $event.value; updateEndTime()" [disabled]="!!form.get('end_time')?.disabled">
                            <mat-option *ngFor="let h of hours" [value]="h">{{ h }}</mat-option>
                        </mat-select>
                    </mat-form-field>
                    <span class="time-separator">:</span>
                    <mat-form-field appearance="outline">
                        <mat-label>MM</mat-label>
                        <mat-select [value]="end_min" panelClass="time-select-panel" (selectionChange)="end_min = $event.value; updateEndTime()" [disabled]="!!form.get('end_time')?.disabled">
                            <mat-option *ngFor="let m of minutes" [value]="m">{{ m }}</mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>
            </div>


            <!-- Modern Task Selection (Chip-based) -->
            <div class="task-selection-container">
                <div class="label-row">
                    <label class="section-label">Select Tasks {{ form.get('tasks')?.value?.length || 0 }}/{{ getTaskLimit() }}</label>
                </div>

                <!-- Glowing Toaster Notification (Centered horizontally, Y near click) -->
                <div class="limit-toast" [class.show]="showLimitError" [style.top.px]="toastY">
                    <mat-icon>warning</mat-icon>
                    <span>Cant add more tasks. Remove one</span>
                </div>

                <mat-selection-list formControlName="tasks" (selectionChange)="onTaskSelectionChange($event)"
                    class="task-chips-container" [multiple]="true" [disabled]="isShiftReadOnly || !!form.get('tasks')?.disabled">
                    <mat-list-option *ngFor="let task of data.tasks" [value]="task.id"
                        [class.selected]="form.get('tasks')?.value?.includes(task.id)" [style.--task-bg]="task.bg_color"
                        [style.--task-fg]="task.fg_color">
                        <div class="modern-chip">
                            <span class="task-name">{{ task.name }}</span>
                            <mat-icon *ngIf="form.get('tasks')?.value?.includes(task.id)"
                                class="check-icon">close</mat-icon>
                        </div>
                    </mat-list-option>
                </mat-selection-list>
            </div>

            <div class="checkbox-row">
                <mat-checkbox formControlName="is_in_charge">In Charge (INC)</mat-checkbox>
            </div>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Comments</mat-label>
                <textarea matInput formControlName="notes" rows="3" placeholder="Add shift notes..."></textarea>
            </mat-form-field>
        </ng-template>
    </form>

    <div mat-dialog-actions class="dialog-actions" style="display: flex; justify-content: space-between; width: 100%;">
        <div class="left-actions" style="display: flex; gap: 8px;">
            <button mat-stroked-button color="warn"
                *ngIf="showDeleteShiftButton"
                (click)="onDeleteShift()">Delete Shift</button>
            <button mat-stroked-button color="warn" *ngIf="showDeleteAttendance" (click)="onDeleteAttendance()">Delete
                Attendance</button>
            <button mat-stroked-button color="primary"
                *ngIf="showFinalizeButton"
                (click)="onFinalize()">Finalize</button>
        </div>
        <div class="right-actions" style="display: flex; gap: 8px;">
            <button mat-button (click)="onCancel()">{{ isAllReadOnly ? 'Close' : 'Cancel' }}</button>
            <button mat-raised-button color="primary" (click)="onSave()" *ngIf="!isAllReadOnly">OK</button>
        </div>
    </div>
</div>